import React, { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Copy, RefreshCw, KeyRound, Webhook, Plus, Play, Pencil, Pause, Trash2, ChevronDown, CheckCircle2, XCircle, Loader2, AlertTriangle, Terminal } from "lucide-react";
import { toast } from "sonner";
import api, { API } from "@/lib/api";

const EVENTS = [
  { id: "call.completed", label: "call.completed", desc: "any call ends" },
  { id: "lead.qualified", label: "lead.qualified", desc: "score ≥ 50" },
  { id: "lead.hot", label: "lead.hot", desc: "score ≥ 80" },
  { id: "call.no_answer", label: "call.no_answer", desc: "call not answered" },
  { id: "lead.not_interested", label: "lead.not_interested", desc: "lead declined" },
  { id: "demo.booked", label: "demo.booked", desc: "demo scheduled" },
];

const copy = (txt) => { navigator.clipboard.writeText(txt); toast.success("Copied to clipboard"); };

function CodeBlock({ label, code, testid }) {
  return (
    <div className="rounded-lg border bg-slate-950 text-slate-100 overflow-hidden" data-testid={testid}>
      <div className="flex items-center justify-between px-3 py-2 border-b border-slate-800 bg-slate-900">
        <span className="text-xs font-medium text-slate-300 flex items-center gap-1.5"><Terminal className="w-3.5 h-3.5" />{label}</span>
        <Button size="sm" variant="ghost" className="h-6 px-2 text-slate-300 hover:text-white hover:bg-slate-800" onClick={() => copy(code)} data-testid={`${testid}-copy`}><Copy className="w-3 h-3 mr-1" />Copy</Button>
      </div>
      <pre className="p-3 text-xs overflow-x-auto whitespace-pre-wrap break-words leading-relaxed"><code>{code}</code></pre>
    </div>
  );
}

function InboundApi({ base }) {
  const [info, setInfo] = useState(null);
  const [fullKey, setFullKey] = useState("");
  const [busy, setBusy] = useState(false);
  const [testOpen, setTestOpen] = useState(false);
  const [testBody, setTestBody] = useState({ mobile: "9876543210", name: "Rahul Sharma", interest: "ERP Software", city: "Mumbai" });
  const [testResp, setTestResp] = useState(null);
  const [testing, setTesting] = useState(false);

  const load = () => api.get("/apikeys").then((r) => { setInfo(r.data); if (r.data.key) setFullKey(r.data.key); });
  useEffect(() => { load(); }, []);

  const regen = async () => {
    if (!window.confirm("Regenerate API key? Your current key will stop working immediately.")) return;
    setBusy(true);
    try { const { data } = await api.post("/apikeys/regenerate"); setFullKey(data.key); setInfo({ ...info, key_prefix: data.key_prefix, is_new: true }); toast.success("New API key generated"); }
    finally { setBusy(false); }
  };

  const runTest = async () => {
    setTesting(true); setTestResp(null);
    try {
      const r = await fetch(`${API}/v1/calls/trigger`, {
        method: "POST", headers: { Authorization: `Bearer ${fullKey}`, "Content-Type": "application/json" },
        body: JSON.stringify(testBody),
      });
      const json = await r.json().catch(() => ({}));
      setTestResp({ status: r.status, body: json });
    } catch (e) { setTestResp({ status: 0, body: { error: String(e) } }); }
    finally { setTesting(false); }
  };

  if (!info) return <div className="py-8 text-center"><Loader2 className="w-5 h-5 animate-spin text-indigo-500 mx-auto" /></div>;

  const keyDisplay = fullKey || `${info.key_prefix}${"•".repeat(16)}`;
  const curl = `curl -X POST ${base}/api/v1/calls/trigger \\
  -H "Authorization: Bearer pk_live_xxx" \\
  -H "Content-Type: application/json" \\
  -d '{"mobile":"9876543210","name":"Rahul","interest":"ERP","city":"Mumbai"}'`;
  const js = `const response = await fetch('${base}/api/v1/calls/trigger', {
  method: 'POST',
  headers: {
    'Authorization': 'Bearer pk_live_xxx',
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({mobile: '9876543210', name: 'Rahul', interest: 'ERP', city: 'Mumbai'})
});`;
  const py = `import requests
response = requests.post(
  '${base}/api/v1/calls/trigger',
  headers={'Authorization': 'Bearer pk_live_xxx'},
  json={'mobile': '9876543210', 'name': 'Rahul', 'interest': 'ERP', 'city': 'Mumbai'}
)`;
  const n8n = `Use HTTP Request node:
Method: POST
URL: ${base}/api/v1/calls/trigger
Auth: Header Auth → Authorization: Bearer pk_live_xxx
Body: JSON with lead data`;
  const reqBody = `{
  "mobile": "9876543210",
  "name": "Rahul Sharma",
  "interest": "ERP Software",
  "city": "Mumbai",
  "email": "rahul@example.com",
  "language": "hi-IN",
  "campaign_id": "optional",
  "metadata": {
    "source": "indiamart",
    "external_lead_id": "IND-123"
  }
}`;
  const statusResp = `{
  "call_id": "call_abc123",
  "status": "completed",
  "outcome": "qualified",
  "score": 85,
  "category": "HOT",
  "duration_secs": 127,
  "summary": "Manufacturing business, needs Odoo ERP for 20-30 users, buying this month.",
  "transcript": "AKRITI: Namaste...\\nCALLER: Haan ji...",
  "called_at": "2026-08-18T10:30:00+05:30"
}`;

  return (
    <div className="space-y-6">
      {/* API Key card */}
      <Card className="p-5 border">
        <div className="flex items-center gap-2 mb-3"><KeyRound className="w-4 h-4 text-indigo-500" /><h4 className="font-semibold font-display">Your API Key</h4></div>
        <div className="flex items-center gap-2 flex-wrap">
          <code className="flex-1 min-w-[240px] font-mono text-sm bg-slate-100 dark:bg-slate-800 rounded-lg px-3 py-2 break-all" data-testid="api-key-value">{keyDisplay}</code>
          <Button size="sm" variant="outline" onClick={() => copy(fullKey || info.key_prefix)} data-testid="api-key-copy"><Copy className="w-3.5 h-3.5 mr-1" />Copy Key</Button>
          <Button size="sm" variant="outline" onClick={regen} disabled={busy} data-testid="api-key-regenerate">{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><RefreshCw className="w-3.5 h-3.5 mr-1" />Regenerate</>}</Button>
        </div>
        {!fullKey && !info.is_new && <p className="text-xs text-slate-500 mt-2">For security only the prefix is shown. Click <b>Regenerate</b> to get a new full key.</p>}
        <p className="text-xs text-amber-600 dark:text-amber-400 mt-2 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" />Keep secret — never share publicly.</p>
      </Card>

      {/* Endpoint 1 */}
      <div className="space-y-3">
        <div className="flex items-center gap-2"><Badge className="bg-emerald-600">POST</Badge><span className="font-medium text-sm">Trigger Single Call</span><Button size="sm" variant="secondary" className="ml-auto" onClick={() => setTestOpen(true)} data-testid="test-api-btn"><Play className="w-3.5 h-3.5 mr-1" />Test API</Button></div>
        <code className="block text-xs bg-slate-100 dark:bg-slate-800 rounded-lg px-3 py-2 break-all">{base}/api/v1/calls/trigger</code>
        <div className="grid md:grid-cols-2 gap-3">
          <CodeBlock label="Request body" code={reqBody} testid="req-body" />
          <div className="space-y-2 text-xs">
            <div className="rounded-lg border border-emerald-200 dark:border-emerald-900 bg-emerald-50 dark:bg-emerald-950/30 p-3"><b>200 OK</b><pre className="mt-1 whitespace-pre-wrap">{`{ "success": true, "call_id": "call_abc123", "lead_id": "lead_xyz456", "message": "Call queued successfully", "status": "queued" }`}</pre></div>
            <div className="rounded-lg border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950/30 p-3"><b>400 / 401</b><pre className="mt-1 whitespace-pre-wrap">{`{ "success": false, "error": "Invalid mobile number" }
{ "success": false, "error": "Invalid or missing API key" }`}</pre></div>
          </div>
        </div>
      </div>

      {/* Endpoint 2 */}
      <div className="space-y-3">
        <div className="flex items-center gap-2"><Badge className="bg-sky-600">GET</Badge><span className="font-medium text-sm">Get Call Status</span></div>
        <code className="block text-xs bg-slate-100 dark:bg-slate-800 rounded-lg px-3 py-2 break-all">{base}/api/v1/calls/{"{call_id}"}</code>
        <CodeBlock label="Response" code={statusResp} testid="status-resp" />
      </div>

      {/* Code examples */}
      <div className="space-y-3">
        <h4 className="font-semibold font-display text-sm">Code examples</h4>
        <div className="grid md:grid-cols-2 gap-3">
          <CodeBlock label="cURL" code={curl} testid="ex-curl" />
          <CodeBlock label="JavaScript" code={js} testid="ex-js" />
          <CodeBlock label="Python" code={py} testid="ex-python" />
          <CodeBlock label="n8n" code={n8n} testid="ex-n8n" />
        </div>
      </div>
      <p className="text-xs text-slate-500">Rate limit: 60 requests per minute per API key (returns 429 if exceeded).</p>

      {/* Test API modal */}
      <Dialog open={testOpen} onOpenChange={setTestOpen}>
        <DialogContent className="max-w-lg" data-testid="test-api-modal">
          <DialogHeader><DialogTitle className="font-display">Test API — Trigger Call</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {["mobile", "name", "interest", "city"].map((f) => (
              <div key={f}><Label className="capitalize">{f}</Label><Input value={testBody[f]} onChange={(e) => setTestBody({ ...testBody, [f]: e.target.value })} data-testid={`test-field-${f}`} /></div>
            ))}
            <Button onClick={runTest} disabled={testing || !fullKey} className="w-full" data-testid="test-api-run">{testing ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send Request"}</Button>
            {!fullKey && <p className="text-xs text-amber-600">Your full key isn't shown after reload. Click <b>Regenerate</b> to get a fresh key, then test.</p>}
            {testResp && (
              <div className="rounded-lg border p-3 text-xs" data-testid="test-api-response">
                <div className="flex items-center gap-2 mb-1 font-medium">{testResp.status >= 200 && testResp.status < 300 ? <CheckCircle2 className="w-4 h-4 text-emerald-500" /> : <XCircle className="w-4 h-4 text-red-500" />}Status {testResp.status}</div>
                <pre className="whitespace-pre-wrap break-words bg-slate-100 dark:bg-slate-800 rounded p-2">{JSON.stringify(testResp.body, null, 2)}</pre>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function WebhookRow({ wh, onChange }) {
  const [openLog, setOpenLog] = useState(false);
  const [logs, setLogs] = useState(null);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const loadLogs = async () => { const { data } = await api.get(`/webhooks/${wh.id}/deliveries`); setLogs(data); };
  const test = async () => { setTesting(true); setTestResult(null); try { const { data } = await api.post(`/webhooks/${wh.id}/test`); setTestResult(data); if (openLog) loadLogs(); } finally { setTesting(false); } };
  const togglePause = async () => { await api.put(`/webhooks/${wh.id}`, { is_active: !wh.is_active }); toast.success(wh.is_active ? "Webhook paused" : "Webhook resumed"); onChange(); };
  const del = async () => { if (!window.confirm(`Delete webhook "${wh.name}"?`)) return; await api.delete(`/webhooks/${wh.id}`); toast.success("Webhook deleted"); onChange(); };

  return (
    <Card className="border p-0 overflow-hidden" data-testid={`webhook-row-${wh.id}`}>
      <div className="p-4 flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2"><span className="font-medium">{wh.name}</span>{wh.is_active ? <Badge className="bg-emerald-500 text-[10px]">Active</Badge> : <Badge variant="secondary" className="text-[10px]">Paused</Badge>}{wh.secret_set && <Badge variant="outline" className="text-[10px]">HMAC</Badge>}</div>
          <div className="text-xs text-slate-500 truncate max-w-[320px] mt-0.5">{wh.url}</div>
          <div className="flex flex-wrap gap-1 mt-1.5">{wh.events.map((e) => <Badge key={e} variant="outline" className="text-[10px]">{e}</Badge>)}</div>
          <div className="text-[11px] text-slate-400 mt-1">Last triggered: {wh.last_triggered_at ? new Date(wh.last_triggered_at).toLocaleString() : "Never"}</div>
        </div>
        <div className="flex gap-1">
          <Button size="sm" variant="outline" onClick={test} disabled={testing} data-testid={`webhook-test-${wh.id}`}>{testing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Play className="w-3.5 h-3.5 mr-1" />Test</>}</Button>
          <Button size="sm" variant="ghost" onClick={() => onChange(wh)} data-testid={`webhook-edit-${wh.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
          <Button size="sm" variant="ghost" onClick={togglePause} data-testid={`webhook-pause-${wh.id}`}><Pause className="w-3.5 h-3.5" /></Button>
          <Button size="sm" variant="ghost" className="text-red-500" onClick={del} data-testid={`webhook-delete-${wh.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
        </div>
      </div>
      {testResult && (
        <div className="px-4 pb-3 text-xs flex items-center gap-2" data-testid={`webhook-test-result-${wh.id}`}>
          {testResult.success ? <CheckCircle2 className="w-4 h-4 text-emerald-500" /> : <XCircle className="w-4 h-4 text-red-500" />}
          <span>Response {testResult.response_code} · {testResult.duration_ms}ms</span>
          <code className="text-slate-500 truncate max-w-[300px]">{String(testResult.response_body).slice(0, 80)}</code>
        </div>
      )}
      <Collapsible open={openLog} onOpenChange={(o) => { setOpenLog(o); if (o) loadLogs(); }}>
        <CollapsibleTrigger asChild><button className="w-full text-left text-xs px-4 py-2 border-t bg-slate-50 dark:bg-slate-800/50 flex items-center gap-1 hover:bg-slate-100" data-testid={`webhook-log-toggle-${wh.id}`}><ChevronDown className="w-3.5 h-3.5" />Delivery log (last 20)</button></CollapsibleTrigger>
        <CollapsibleContent>
          <div className="px-4 py-2 text-xs">
            {!logs ? <Loader2 className="w-4 h-4 animate-spin" /> : logs.length === 0 ? <span className="text-slate-400">No deliveries yet.</span> : (
              <table className="w-full">
                <thead><tr className="text-slate-400 text-left"><th className="py-1">Time</th><th>Event</th><th>Code</th><th>Duration</th><th>Attempt</th></tr></thead>
                <tbody>{logs.map((d) => (
                  <tr key={d.id} className="border-t border-slate-100 dark:border-slate-800"><td className="py-1">{new Date(d.delivered_at).toLocaleTimeString()}</td><td>{d.event}</td><td className={d.success ? "text-emerald-600" : "text-red-500"}>{d.response_code}</td><td>{d.duration_ms}ms</td><td>#{d.attempt_number}</td></tr>
                ))}</tbody>
              </table>
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}

function OutboundWebhooks() {
  const [list, setList] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", url: "", events: ["call.completed"], secret: "" });

  const load = () => api.get("/webhooks").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm({ name: "", url: "", events: ["call.completed"], secret: "" }); setFormOpen(true); };
  const openEdit = (wh) => { if (!wh?.id) return; setEditing(wh); setForm({ name: wh.name, url: wh.url, events: wh.events, secret: "" }); setFormOpen(true); };

  const toggleEvent = (id) => setForm((f) => ({ ...f, events: f.events.includes(id) ? f.events.filter((e) => e !== id) : [...f.events, id] }));

  const save = async () => {
    if (!form.url.startsWith("https://")) { toast.error("URL must start with https://"); return; }
    if (form.events.length === 0) { toast.error("Select at least one event"); return; }
    try {
      if (editing) await api.put(`/webhooks/${editing.id}`, form);
      else await api.post("/webhooks", form);
      toast.success(editing ? "Webhook updated" : "Webhook added");
      setFormOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save webhook"); }
  };

  if (!list) return <div className="py-8 text-center"><Loader2 className="w-5 h-5 animate-spin text-indigo-500 mx-auto" /></div>;

  const samplePayload = `{
  "event": "call.completed",
  "timestamp": "2026-08-18T10:35:27+05:30",
  "call_id": "call_abc123",
  "lead": { "mobile": "9876543210", "name": "Rahul Sharma", "email": "rahul@example.com", "city": "Mumbai", "interest": "ERP Software", "language": "hi-IN" },
  "result": { "outcome": "qualified", "score": 85, "category": "HOT", "qualification_done": true, "demo_booked": false, "not_interested": false, "duration_secs": 127, "criterion_answers": { "business_type": "Manufacturing", "service_needed": "Odoo ERP", "user_count": "20-30", "timeline": "This month" } },
  "summary": "Manufacturing business needs Odoo ERP for 20-30 users. Ready to buy this month.",
  "metadata": {}
}`;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-xs text-slate-500">We POST a JSON payload to your URL after every matching event. Auto-retry 3× (5min, 30min, 2hr) if response ≠ 200.</p>
        <Button size="sm" onClick={openNew} data-testid="add-webhook-btn"><Plus className="w-3.5 h-3.5 mr-1" />Add Webhook</Button>
      </div>

      {list.length === 0 ? (
        <Card className="border border-dashed p-8 text-center text-sm text-slate-500" data-testid="webhooks-empty"><Webhook className="w-8 h-8 mx-auto mb-2 text-slate-300" />No webhooks yet. Add one to receive real-time call events.</Card>
      ) : (
        <div className="space-y-3">{list.map((wh) => <WebhookRow key={wh.id} wh={wh} onChange={(arg) => (arg?.id ? openEdit(arg) : load())} />)}</div>
      )}

      <CodeBlock label="Sample payload sent to your URL" code={samplePayload} testid="webhook-payload" />
      <p className="text-xs text-slate-500">If a secret key is set, we add header <code>X-PK-Signature: sha256=HMAC_SHA256(payload, secret)</code>.</p>

      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-lg" data-testid="webhook-form">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Edit Webhook" : "Add Webhook"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Webhook Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="My Zapier, HubSpot" data-testid="webhook-name" /></div>
            <div><Label>URL</Label><Input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://..." data-testid="webhook-url" /></div>
            <div>
              <Label>Events</Label>
              <div className="space-y-2 mt-1.5">{EVENTS.map((e) => (
                <label key={e.id} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`webhook-event-${e.id}`}>
                  <Checkbox checked={form.events.includes(e.id)} onCheckedChange={() => toggleEvent(e.id)} />
                  <code className="text-xs">{e.label}</code><span className="text-xs text-slate-400">— {e.desc}</span>
                </label>
              ))}</div>
            </div>
            <div><Label>Secret Key (optional — for HMAC verification)</Label><Input value={form.secret} onChange={(e) => setForm({ ...form, secret: e.target.value })} placeholder={editing?.secret_set ? "•••••• (saved — leave blank to keep)" : "your signing secret"} data-testid="webhook-secret" /></div>
            <Button onClick={save} className="w-full" data-testid="webhook-save">{editing ? "Save Changes" : "Save Webhook"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function ApiWebhooksPanel() {
  const [base, setBase] = useState("");
  useEffect(() => { api.get("/apikeys").then((r) => setBase(r.data.public_base_url || "")); }, []);

  return (
    <Card className="border shadow-none p-6 mt-8" data-testid="api-webhooks-section">
      <div className="flex items-center gap-2 mb-1"><Webhook className="w-5 h-5 text-indigo-500" /><h2 className="text-lg font-semibold font-display">API & Webhooks</h2></div>
      <p className="text-sm text-slate-500 mb-5">Trigger calls programmatically and receive real-time call events in your own systems.</p>
      <Tabs defaultValue="inbound">
        <TabsList data-testid="api-webhooks-tabs">
          <TabsTrigger value="inbound" data-testid="tab-inbound-api">Inbound API</TabsTrigger>
          <TabsTrigger value="outbound" data-testid="tab-outbound-webhooks">Outbound Webhooks</TabsTrigger>
        </TabsList>
        <TabsContent value="inbound" className="mt-5"><InboundApi base={base} /></TabsContent>
        <TabsContent value="outbound" className="mt-5"><OutboundWebhooks /></TabsContent>
      </Tabs>
    </Card>
  );
}
